import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { I18nProvider } from "./i18n";
import Home from "./pages/Home";
import Schools from "./pages/Schools";
import SchoolDetail from "./pages/SchoolDetail";
import Donate from "./pages/Donate";
import Login from "./pages/Login";
import RegisterSchool from "./pages/RegisterSchool";
import AuthCallback from "./pages/AuthCallback";
import DonorDashboard from "./pages/DonorDashboard";
import SchoolDashboard from "./pages/SchoolDashboard";
import AdminPanel from "./pages/AdminPanel";
import AdminLogin from "./pages/AdminLogin";
import Welcome from "./pages/Welcome";
import AudioApp from "./audio_comics/AudioApp";
import About from "./pages/About";
import Impact from "./pages/Impact";
import LegacyRedirect from "./pages/LegacyRedirect";
import EComics from "./pages/EComics";
import EComicsVolume from "./pages/EComicsVolume";
import EComicsReader from "./pages/EComicsReader";
import EComicsLibrary from "./pages/EComicsLibrary";

// Match legacy audio-comic URLs: /v1v1.html through /v10v10.html
// plus malformed variants missing the dot (e.g. /v1v6html, /v10v6html).
const LEGACY_AUDIO_RE = /^\/v([1-9]|10)v([1-9]|10)\.?html$/i;

function NotFoundOrLegacy() {
  const location = useLocation();
  if (LEGACY_AUDIO_RE.test(location.pathname)) {
    return <LegacyRedirect />;
  }
  return <Navigate to="/welcome" replace />;
}

function AppRouter() {
  const location = useLocation();
  // Handle Emergent OAuth return (session_id in URL fragment) before normal routing
  if (location.hash?.includes("session_id=")) {
    // If returning to the audio app, let the audio callback handle it
    if (location.pathname.startsWith("/audio-comics/")) {
      return (
        <Routes>
          <Route path="/audio-comics/*" element={<AudioApp />} />
        </Routes>
      );
    }
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/audio-comics/*" element={<AudioApp />} />
      <Route path="/" element={<Navigate to="/welcome" replace />} />
      <Route path="/welcome" element={<Welcome />} />
      <Route path="/ecomics" element={<EComics />} />
      <Route path="/ecomics/volume/:volumeId" element={<EComicsVolume />} />
      <Route path="/ecomics/read/:volumeId/:vachanaId" element={<EComicsReader />} />
      <Route path="/ecomics/library" element={<EComicsLibrary />} />
      <Route path="/home" element={<Home />} />
      <Route path="/schools" element={<Schools />} />
      <Route path="/schools/:schoolId" element={<SchoolDetail />} />
      <Route path="/donate/:schoolId" element={<Donate />} />
      <Route path="/sponsor-books" element={<Navigate to="/schools" replace />} />
      <Route path="/sponsor-books/:schoolId" element={<Donate />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register-school" element={<RegisterSchool />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/donor" element={<DonorDashboard />} />
      <Route path="/school" element={<SchoolDashboard />} />
      <Route path="/admin" element={<AdminPanel />} />
      <Route path="/admin-login" element={<AdminLogin />} />
      <Route path="/about" element={<About />} />
      <Route path="/impact" element={<Impact />} />
      <Route path="*" element={<NotFoundOrLegacy />} />
    </Routes>
  );
}

function App() {
  // Strip any external branding nodes that may be injected by the platform shell.
  // Runs once on mount only — avoids interfering with React reconciliation.
  useEffect(() => {
    document
      .querySelectorAll('[id^="emergent-"], [class*="emergent"], [data-emergent]')
      .forEach((el) => { try { el.remove(); } catch { /* noop */ } });
  }, []);

  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
